import React, { useState, useEffect, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { signInWithGoogle } from '../lib/auth';
import LoadingIndicator from '../components/LoadingIndicator';
import { AlertCircle, Mail, Lock, Loader, LogIn, Phone } from 'lucide-react';

export default function Login() {
  const [formData, setFormData] = useState({ email: '', password: '', phone: '' });
  const [error, setError] = useState<string | null | JSX.Element>(null);
  const [resetSent, setResetSent] = useState(false);
  const [confirmationNotice, setConfirmationNotice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingAuth, setProcessingAuth] = useState(false);
  const [authMethod, setAuthMethod] = useState<'email' | 'phone' | 'otp'>('email');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we have a confirmation notice from registration
    if (location.state && location.state.confirmationNotice) {
      setConfirmationNotice(true);
    }
    
    // Check for confirmed=true in URL params
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('confirmed') === 'true') {
      setConfirmationNotice(true);
    }
  }, [location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      if (authMethod === 'email') {
        console.log("Attempting to sign in with password");
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });
        
        if (error) {
          console.error("Login error:", error);
          setError(error.message);
        } else {
          console.log("Login successful:", data);
          
          // Clean up URL parameters
          const currentUrl = new URL(window.location.href);
          currentUrl.search = '';
          window.history.replaceState({}, '', currentUrl.toString());
          
          // Check if user needs to complete profile
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('full_name, location, bio')
            .eq('id', data.user.id)
            .single();
            
          if (profileError || !profile || !profile.full_name || !profile.location) {
            // Redirect to profile completion page
            window.location.href = '/profile-completion';
          } else {
            // Redirect to home page
            window.location.href = '/';
          }
        }
      } else if (authMethod === 'phone') {
        // Sign in with phone and password
        const { data, error } = await supabase.auth.signInWithPassword({
          phone: formData.phone,
          password: formData.password,
        });
        
        if (error) {
          console.error("Phone login error:", error);
          setError(error.message);
        } else {
          console.log("Phone login successful:", data);
          
          // Check if user needs to complete profile
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('full_name, location, bio')
            .eq('id', data.user.id)
            .single();
            
          if (profileError || !profile || !profile.full_name || !profile.location) {
            // Redirect to profile completion page
            window.location.href = '/profile-completion';
          } else {
            // Redirect to home page
            window.location.href = '/';
          }
        }
      } else if (authMethod === 'otp') {
        // Verify OTP
        const { data, error } = await supabase.auth.verifyOtp({
          phone: formData.phone,
          token: otp,
          type: 'sms'
        });
        
        if (error) {
          console.error("OTP verification error:", error);
          setError(error.message);
        } else {
          console.log("OTP verification successful:", data);
          
          // Check if user needs to complete profile
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('full_name, location, bio')
            .eq('id', data.user.id)
            .single();
            
          if (profileError || !profile || !profile.full_name || !profile.location) {
            // Redirect to profile completion page
            window.location.href = '/profile-completion';
          } else {
            // Redirect to home page
            window.location.href = '/';
          }
        }
      }
    } catch (error: any) {
      console.error("Unexpected login error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const result = await signInWithGoogle();
      
      if (result.error) {
        setError(result.error);
      } else if (result.url) {
        // User will be redirected to Google's login page
        window.location.href = result.url;
      }
    } catch (error: any) {
      console.error("Unexpected Google login error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setError(null);
    setResetSent(false);
    setLoading(true);
    
    if (!formData.email) {
      setError('Bitte gib zuerst deine E-Mail-Adresse ein.');
      setLoading(false);
      return;
    }
    
    try {
      // Get the current origin for the redirect URL
      const baseUrl = window.location.origin;
        
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${baseUrl}/update-password`
      });
      
      if (error) {
        setError(error.message);
      } else {
        setResetSent(true);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (processingAuth) {
    return <LoadingIndicator message="Authentication in progress..." />;
  }

  return (
    <div className="max-w-md mx-auto p-6 mt-10 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4 text-center">Login</h2>
     
      {confirmationNotice && (
        <div className="mb-4 p-3 bg-green-100 border-l-4 border-green-500 text-green-700 text-sm">
          Bitte bestätige deine E-Mail-Adresse. Wir haben dir einen Bestätigungslink gesendet.
        </div>
      )}
     
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      )}
     
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Authentication Method Selector */}
        <div className="flex border rounded-md overflow-hidden mb-4">
          <button
            type="button"
            onClick={() => setAuthMethod('email')}
            className={`flex-1 py-2 ${authMethod === 'email' ? 'bg-orange-500 text-white' : 'bg-gray-100'}`}
          >
            E-Mail
          </button>
          <button
            type="button"
            onClick={() => setAuthMethod('phone')}
            className={`flex-1 py-2 ${authMethod === 'phone' || authMethod === 'otp' ? 'bg-orange-500 text-white' : 'bg-gray-100'}`}
          >
            Telefon
          </button>
        </div>

        {authMethod === 'email' && (
          <>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="email"
                name="email"
                placeholder="E-Mail"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 pl-10 border rounded-md"
                required
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="password"
                name="password"
                placeholder="Passwort"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-2 pl-10 border rounded-md"
                required
              />
            </div>
          </>
        )}

        {authMethod === 'phone' && (
          <>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                name="phone"
                placeholder="Telefonnummer (z.B. +491701234567)"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-2 pl-10 border rounded-md"
                required
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="password"
                name="password"
                placeholder="Passwort"
                value={formData.password}
                onChange={handleChange}
                className="w-full p-2 pl-10 border rounded-md"
                required
              />
            </div>
          </>
        )}

        {authMethod === 'otp' && (
          <>
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600">
                Ein Einmalpasswort wurde an {formData.phone} gesendet.
              </p>
            </div>
            <input
              type="text"
              placeholder="Einmalpasswort eingeben"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              className="w-full p-2 border rounded-md"
              required
            />
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setOtpSent(false);
                  setAuthMethod('phone');
                }}
                className="text-orange-500 hover:underline text-sm"
              >
                Andere Telefonnummer verwenden
              </button>
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={loading}
          className={`w-full bg-orange-500 text-white py-2 rounded hover:bg-orange-600 transition flex items-center justify-center space-x-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {loading ? (
            <>
              <Loader className="h-5 w-5 animate-spin" />
              <span>Wird angemeldet...</span>
            </>
          ) : (
            <>
              <LogIn className="h-5 w-5" />
              <span>{authMethod === 'otp' ? 'Verifizieren' : authMethod === 'phone' ? 'Login' : 'Login'}</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-4 text-center">
        <p className="text-gray-600 mb-4">Oder anmelden mit:</p>
        <button
          onClick={handleGoogleSignUp}
          disabled={loading}
          className="w-full flex items-center justify-center bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 transition"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Mit Google anmelden
        </button>
      </div>

      <div className="mt-4 text-center">
        <button
          onClick={handlePasswordReset}
          disabled={loading}
          className="text-sm text-orange-600 hover:underline"
          type="button"
        >
          Passwort vergessen?
        </button>
        {resetSent && (
          <p className="text-green-600 mt-2 text-sm">
            Eine E-Mail zum Zurücksetzen wurde gesendet.
          </p>
        )}
      </div>
      
      <div className="mt-6 text-center text-sm text-gray-600">
        <p>Noch kein Konto? <Link to="/register" className="text-orange-600 hover:underline">Registrieren</Link></p>
      </div>
    </div>
  );
}